import { useState, useEffect, useRef } from 'react';
import { MoveRight, Store } from 'lucide-react';
import { BrandAiEnabled, BrandShadowMode } from './brand-flags';
import { TimezoneMismatchDialog } from './timezone-mismatch-dialog';
import { TimeSelect } from './operating-hours';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useAiSettings, useUpdateAiSettings, useStoreTimezone, useIntegrations } from '@/hooks';
import { useBrandSettings, useUpdateBrandSettings } from '@/hooks/use-brand-settings';
import { INTEGRATIONS } from '@/modules/integrations/constants';
import { KIM_HELPDESK, connectedHelpdesks, isKimHelpdesk } from '@/modules/integrations/provider-kind';
import type { IntegrationType } from '@/modules/integrations/types';
import { buildDefaultAiSettings } from '@/lib/ai-settings-defaults';
import {
	Field,
	SaveBar,
	SETTINGS_INPUT_CLASS,
	SettingsSelect,
	SettingsBody,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsDivider,
	SettingsError,
	SettingsPage,
	SettingsSection,
	ToggleRow
} from '@/modules/settings';
import type { AiSettings, BusinessHours } from '@/types';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const TIMEZONES: string[] = Intl.supportedValuesOf('timeZone');
const DEFAULT_BUSINESS_HOURS: BusinessHours = { days: [], startTime: '', endTime: '' };
const helpdeskLabel = (value: string) => INTEGRATIONS[value as IntegrationType]?.name ?? value;
const SECONDS_PER_MINUTE = 60;
const MIN_REFRESH_MINUTES = 1;
const MAX_REFRESH_MINUTES = 1440;
const DEFAULT_REFRESH_MINUTES = 30;

/** Seven round day-of-week chip buttons */
function DayChips({ selected, onToggle, disabled }: { selected: number[]; onToggle: (dayIndex: number) => void; disabled?: boolean }) {
	return (
		<div className='flex gap-3'>
			{DAY_LABELS.map((label, idx) => {
				const active = selected.includes(idx);
				return (
					<button
						key={idx}
						type='button'
						disabled={disabled}
						aria-pressed={active}
						onClick={() => onToggle(idx)}
						className={cn(
							'flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-500 text-[12px] transition-colors disabled:cursor-not-allowed',
							active ? 'bg-ink-strong text-white' : 'cursor-pointer bg-white text-ink-strong hover:bg-gray-100'
						)}
					>
						{label}
					</button>
				);
			})}
		</div>
	);
}

/**
 * My Brand — facts about the brand: Kim on/off and shadow mode, its helpdesks and widget refresh,
 * and its timezone and team's business hours (Kim's operating schedule is read in the same timezone).
 */
export default function BrandSettingsPage() {
	const { data: settings, isLoading, isError } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { data: storeTimezoneData } = useStoreTimezone();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	const { data: brandSettings, isError: isBrandSettingsError } = useBrandSettings();
	const updateBrandSettings = useUpdateBrandSettings();
	const { data: integrations, isLoading: integrationsLoading } = useIntegrations();
	const [helpdesk, setHelpdesk] = useState('');
	const [savedHelpdesk, setSavedHelpdesk] = useState('');

	const connected = connectedHelpdesks(integrations ?? []);
	// The primary is the external helpdesk; Kim Helpdesk is the primary only when it is the sole one.
	const externals = connected.filter((value) => !isKimHelpdesk(value));
	const primaryChoices = externals.length > 0 ? externals : connected;
	const kimConnected = connected.includes(KIM_HELPDESK);
	// Until a brand picks one, its primary is the account's most recently connected.
	const currentHelpdesk = brandSettings?.helpdeskProvider ?? primaryChoices[0] ?? '';

	useEffect(() => {
		setHelpdesk(currentHelpdesk);
		setSavedHelpdesk(currentHelpdesk);
	}, [currentHelpdesk]);

	// The widget's refresh frequency, edited in minutes and stored in seconds.
	// The field renders only once brand settings load, so it never edits a made-up baseline.
	const currentRefreshMinutes = brandSettings
		? String(brandSettings.refreshThreshold ? Math.round(brandSettings.refreshThreshold / SECONDS_PER_MINUTE) : DEFAULT_REFRESH_MINUTES)
		: '';
	const [refreshMinutes, setRefreshMinutes] = useState(currentRefreshMinutes);
	const [savedRefreshMinutes, setSavedRefreshMinutes] = useState(currentRefreshMinutes);

	useEffect(() => {
		setRefreshMinutes(currentRefreshMinutes);
		setSavedRefreshMinutes(currentRefreshMinutes);
	}, [currentRefreshMinutes]);

	// The primary choices, plus the saved value if it is no longer among them.
	const helpdeskOptions = [...new Set([...primaryChoices, savedHelpdesk].filter(Boolean))];

	// The store's timezone (from Shopify) is the source of truth; nothing saved shows '' until a pre-fill or a pick.
	const [timezone, setTimezone] = useState('');
	const [savedTimezone, setSavedTimezone] = useState('');
	const [timezoneError, setTimezoneError] = useState<string | null>(null);
	// True only while the current value is an untouched Shopify pre-fill.
	const [isTimezonePrefilled, setIsTimezonePrefilled] = useState(false);
	const [isMismatchDialogOpen, setIsMismatchDialogOpen] = useState(false);
	// One pre-fill attempt per loaded settings snapshot, so a late Shopify answer never clobbers an edit.
	const prefillAttemptedRef = useRef(false);
	const [businessHours, setBusinessHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
	const [savedBusinessHours, setSavedBusinessHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);

	useEffect(() => {
		const savedTz = settings?.operatingHours?.timezone || '';
		const bh = settings?.businessHours ?? DEFAULT_BUSINESS_HOURS;
		setTimezone(savedTz);
		setSavedTimezone(savedTz);
		setBusinessHours(bh);
		setSavedBusinessHours(bh);
		setIsTimezonePrefilled(false);
		setTimezoneError(null);
		prefillAttemptedRef.current = false;
	}, [settings]);

	// Pre-fill from the Shopify store timezone, once, and only when nothing is saved yet.
	const persistedTimezone = settings?.operatingHours?.timezone || '';
	useEffect(() => {
		if (prefillAttemptedRef.current || isLoading || persistedTimezone || !storeTimezoneData) return;
		prefillAttemptedRef.current = true;
		if (storeTimezoneData.timezone) {
			setTimezone(storeTimezoneData.timezone);
			setIsTimezonePrefilled(true);
		}
	}, [storeTimezoneData, persistedTimezone, isLoading]);

	const isHoursDirty = timezone !== savedTimezone || JSON.stringify(businessHours) !== JSON.stringify(savedBusinessHours);
	const isHelpdeskDirty = helpdesk !== savedHelpdesk;
	const isRefreshDirty = refreshMinutes !== savedRefreshMinutes;
	const isBrandSettingsDirty = isHelpdeskDirty || isRefreshDirty;
	const isDirty = isHoursDirty || isBrandSettingsDirty;
	const refreshValue = Number(refreshMinutes);
	const refreshInvalid = !Number.isInteger(refreshValue) || refreshValue < MIN_REFRESH_MINUTES || refreshValue > MAX_REFRESH_MINUTES;
	const isSaving = updateMutation.isPending || updateBrandSettings.isPending;
	const businessHoursInvalid = Boolean(businessHours.startTime && businessHours.endTime) && businessHours.startTime >= businessHours.endTime;
	// The browser's list leaves out aliases such as UTC; keep a saved one selectable so the box shows the real value.
	const timezoneOptions = timezone && !TIMEZONES.includes(timezone) ? [timezone, ...TIMEZONES] : TIMEZONES;

	const handleBusinessDayToggle = (idx: number) => {
		setBusinessHours((prev) => ({
			...prev,
			days: prev.days.includes(idx) ? prev.days.filter((d) => d !== idx) : [...prev.days, idx]
		}));
	};

	const handleCancel = () => {
		setTimezone(savedTimezone);
		setTimezoneError(null);
		setIsTimezonePrefilled(false);
		setBusinessHours(savedBusinessHours);
		setHelpdesk(savedHelpdesk);
		setRefreshMinutes(savedRefreshMinutes);
	};

	/** Writes whichever of timezone/business hours and helpdesk/refresh frequency changed; Kim's operating schedule stays as saved. */
	const persist = async (timezoneToSave: string) => {
		let hoursSaved = false;
		try {
			if (isHoursDirty) {
				const base = buildDefaultAiSettings(settings);
				const payload: AiSettings = { ...base, operatingHours: { ...base.operatingHours, timezone: timezoneToSave }, businessHours };
				await updateMutation.mutateAsync(payload);
				setTimezone(timezoneToSave);
				setSavedTimezone(timezoneToSave);
				setSavedBusinessHours(businessHours);
				setIsTimezonePrefilled(false);
				hoursSaved = true;
			}
			if (isBrandSettingsDirty) {
				await updateBrandSettings.mutateAsync({
					...(isHelpdeskDirty && { helpdeskProvider: helpdesk || null }),
					...(isRefreshDirty && { refreshThreshold: refreshValue * SECONDS_PER_MINUTE })
				});
				setSavedHelpdesk(helpdesk);
				setSavedRefreshMinutes(refreshMinutes);
			}
			toast.success('Brand settings saved');
		} catch (error) {
			console.error('[BrandSettingsPage.persist] failed to save brand settings', { timezone: timezoneToSave, error });
			toast.error(
				hoursSaved
					? 'Timezone and hours saved, but the helpdesk and widget settings were not. Please try again.'
					: 'Failed to save brand settings. Please try again.'
			);
		}
	};

	const handleSave = async () => {
		if (!isDirty) return;
		if (isRefreshDirty && refreshInvalid) {
			toast.error(`Widget refresh frequency must be ${MIN_REFRESH_MINUTES}–${MAX_REFRESH_MINUTES} minutes.`);
			return;
		}
		if (!isHoursDirty) {
			await persist(timezone);
			return;
		}
		if (!timezone) {
			setTimezoneError('Select a timezone before saving.');
			return;
		}
		if (businessHoursInvalid) {
			toast.error('Business hours must end after they start.');
			return;
		}
		setTimezoneError(null);
		// Only a changed timezone is checked against Shopify; a loading or failed lookup never blocks the save.
		const shopifyTimezone = storeTimezoneData?.timezone ?? null;
		if (timezone !== savedTimezone && shopifyTimezone && shopifyTimezone !== timezone) {
			setIsMismatchDialogOpen(true);
			return;
		}
		await persist(timezone);
	};

	return (
		<SettingsPage title='My Brand'>
			<SettingsBody>
				<SettingsSection label='Kim AI'>
					<SettingsCard>
						<BrandAiEnabled />
						<SettingsDivider />
						<BrandShadowMode />
					</SettingsCard>
				</SettingsSection>

				<SettingsSection label='Helpdesk'>
					{/* Wait for both: before they load, the primary would show a guess from the connected helpdesks. */}
					{(!brandSettings && !isBrandSettingsError) || integrationsLoading ? (
						<SettingsCardSkeleton rows={3} />
					) : (
						<SettingsCard>
							<Field label='Primary helpdesk' htmlFor='brand-helpdesk' hint='Which of your connected helpdesks this brand uses.'>
								<SettingsSelect
									id='brand-helpdesk'
									value={helpdesk}
									onValueChange={setHelpdesk}
									options={helpdeskOptions.map((value) => ({ value, label: helpdeskLabel(value) }))}
									placeholder={helpdeskOptions.length === 0 ? 'No helpdesk connected' : 'Select a helpdesk'}
									disabled={!canEdit || helpdeskOptions.length === 0}
								/>
							</Field>
							{!isKimHelpdesk(helpdesk) && (
								<Field
									label='Secondary helpdesk'
									htmlFor='brand-secondary-helpdesk'
									hint={
										kimConnected
											? 'Always Kim Helpdesk: its live chat runs beside your primary helpdesk.'
											: 'Always Kim Helpdesk. Connect it in Integrations to add live chat beside your primary helpdesk.'
									}
								>
									<input
										id='brand-secondary-helpdesk'
										value={helpdeskLabel(KIM_HELPDESK)}
										disabled
										readOnly
										className={cn(SETTINGS_INPUT_CLASS, 'bg-gray-50 disabled:opacity-100')}
									/>
								</Field>
							)}
							<Field
								label='Widget refresh frequency (minutes)'
								htmlFor='brand-widget-refresh'
								hint={`Range ${MIN_REFRESH_MINUTES}–${MAX_REFRESH_MINUTES} min. Default ${DEFAULT_REFRESH_MINUTES}.`}
								error={isRefreshDirty && refreshInvalid ? `Enter ${MIN_REFRESH_MINUTES}–${MAX_REFRESH_MINUTES} minutes.` : undefined}
							>
								{brandSettings ? (
									<input
										id='brand-widget-refresh'
										type='number'
										min={MIN_REFRESH_MINUTES}
										max={MAX_REFRESH_MINUTES}
										step={1}
										value={refreshMinutes}
										disabled={!canEdit}
										onChange={(e) => setRefreshMinutes(e.target.value)}
										className={cn(SETTINGS_INPUT_CLASS, isRefreshDirty && refreshInvalid && 'border-red-400')}
									/>
								) : isBrandSettingsError ? (
									<p className='text-[12px] leading-4 text-red-600'>Failed to load the widget setting. Please try again later.</p>
								) : (
									<div className='h-10 w-full animate-pulse rounded-[12px] bg-gray-100' />
								)}
							</Field>
						</SettingsCard>
					)}
				</SettingsSection>

				{/* Timezone and the team's working window; Kim's operating schedule (Ingestion) is read in this timezone. */}
				<SettingsSection label='Timezone & business hours'>
					{isError ? (
						<SettingsError />
					) : isLoading ? (
						<SettingsCardSkeleton rows={2} />
					) : (
						<SettingsCard>
							<Field
								label='Timezone'
								htmlFor='brand-timezone'
								error={timezoneError ?? undefined}
								hint={
									isTimezonePrefilled ? (
										<span className='flex items-center gap-1'>
											<Store className='size-[11px] shrink-0' />
											Pre-filled from your Shopify store. Review and save.
										</span>
									) : (
										"Always set the time zone to match the client's. Business hours and Kim's operating hours use it."
									)
								}
							>
								<SettingsSelect
									id='brand-timezone'
									value={timezone}
									onValueChange={(value) => {
										setTimezone(value);
										setTimezoneError(null);
										setIsTimezonePrefilled(false);
									}}
									options={timezoneOptions.map((tz) => ({ value: tz, label: tz }))}
									placeholder="Select the store's timezone"
									disabled={!canEdit}
									invalid={Boolean(timezoneError)}
									className={cn(timezoneError && 'border-red-400')}
								/>
							</Field>
							<SettingsDivider />
							<ToggleRow
								title='Business hours'
								description='When your team works. First Acknowledgement Response, Brand Health and Sentinel Analytics use it.'
							/>
							<Field label='Days'>
								<DayChips selected={businessHours.days} onToggle={handleBusinessDayToggle} disabled={!canEdit} />
							</Field>
							<Field label='Time' error={businessHoursInvalid ? 'Business hours must end after they start.' : undefined}>
								<div className='flex w-full items-center gap-2.5'>
									<TimeSelect
										value={businessHours.startTime}
										onChange={(v) => setBusinessHours((prev) => ({ ...prev, startTime: v }))}
										placeholder='From'
										invalid={businessHoursInvalid}
									/>
									<MoveRight className='size-3 shrink-0 text-gray-800' />
									<TimeSelect
										value={businessHours.endTime}
										onChange={(v) => setBusinessHours((prev) => ({ ...prev, endTime: v }))}
										placeholder='To'
										invalid={businessHoursInvalid}
									/>
								</div>
							</Field>
						</SettingsCard>
					)}
				</SettingsSection>
			</SettingsBody>

			<SaveBar visible={isDirty} saving={isSaving} canSave={canEdit} onCancel={handleCancel} onSave={() => void handleSave()} />

			<TimezoneMismatchDialog
				open={isMismatchDialogOpen}
				shopifyTimezone={storeTimezoneData?.timezone ?? ''}
				selectedTimezone={timezone}
				onOpenChange={setIsMismatchDialogOpen}
				onKeepSelected={() => {
					setIsMismatchDialogOpen(false);
					void persist(timezone);
				}}
				onUseShopify={() => {
					setIsMismatchDialogOpen(false);
					const shopifyTimezone = storeTimezoneData?.timezone;
					if (shopifyTimezone) void persist(shopifyTimezone);
				}}
			/>
		</SettingsPage>
	);
}
